import { ArrowRight, CheckCircle2, Play } from "lucide-react";
import demoVideo from "../assets/howitworks.webm";

interface HeroProps {
	badge: string;
	heroLineOne: string;
	heroLineTwo: string;
	heroDescription: string;
}

const TRUST_POINTS = [
	"Record screen, webcam, and audio",
	"Edit in the same app",
	"Export polished MP4s and GIFs",
];

const QUICK_STATS = [
	{ label: "Platforms", value: "macOS, Windows, Linux" },
	{ label: "Exports", value: "MP4, GIF, 4K-ready" },
	{ label: "Built for", value: "demos, bug reports, tutorials" },
];

export function Hero({ badge, heroLineOne, heroLineTwo, heroDescription }: HeroProps) {
	return (
		<section className="relative overflow-hidden pb-20 pt-28 md:pb-28 md:pt-40">
			<div className="absolute inset-0 pointer-events-none">
				<div className="absolute inset-x-0 top-0 h-[560px] bg-[radial-gradient(circle_at_top,rgba(16,185,129,0.18),transparent_45%)]" />
				<div className="absolute left-1/2 top-32 h-[460px] w-[820px] -translate-x-1/2 rounded-full bg-brand-500/[0.07] blur-[120px]" />
				<div className="absolute right-[8%] top-24 h-[260px] w-[260px] rounded-full bg-emerald-400/[0.08] blur-[100px]" />
			</div>

			<div className="relative mx-auto max-w-6xl px-6">
				<div className="grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10">
					<div>
						<div className="mb-6 inline-flex items-center gap-2 rounded-full border border-brand-500/20 bg-brand-500/10 px-4 py-1.5 text-xs font-medium text-brand-300">
							<span className="h-1.5 w-1.5 rounded-full bg-brand-400 animate-pulse" />
							{badge}
						</div>

						<h1 className="max-w-3xl text-4xl font-extrabold leading-[1.02] tracking-tight sm:text-5xl md:text-7xl">
							{heroLineOne} <span className="mt-2 block gradient-text">{heroLineTwo}</span>
						</h1>

						<p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/58 md:text-xl">
							{heroDescription}
						</p>

						<div className="mt-8 flex flex-wrap gap-3 text-sm text-white/60">
							{TRUST_POINTS.map((point) => (
								<div
									key={point}
									className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-3 py-2"
								>
									<CheckCircle2 size={16} className="text-brand-400" />
									{point}
								</div>
							))}
						</div>

						<div className="mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
							<a
								href="#download"
								className="group inline-flex items-center gap-2 rounded-xl bg-brand-500 px-7 py-3.5 text-base font-semibold text-white transition-all hover:bg-brand-400 hover:shadow-lg hover:shadow-brand-500/25"
							>
								Download Clapio
								<ArrowRight
									size={18}
									className="transition-transform group-hover:translate-x-0.5"
								/>
							</a>
							<a
								href="#preview"
								className="group inline-flex items-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.05] px-7 py-3.5 text-base font-medium text-white/85 transition-all hover:bg-white/[0.08]"
							>
								<Play size={16} className="text-brand-400" />
								Preview the workflow
							</a>
						</div>

						<div className="mt-10 grid gap-3 sm:grid-cols-3">
							{QUICK_STATS.map((stat) => (
								<div key={stat.label} className="glass-card px-4 py-4">
									<div className="text-[11px] uppercase tracking-[0.18em] text-white/35">
										{stat.label}
									</div>
									<div className="mt-2 text-sm font-medium text-white/85">{stat.value}</div>
								</div>
							))}
						</div>
					</div>

					<div className="relative">
						<div className="absolute -inset-6 rounded-[2rem] bg-brand-500/10 blur-3xl" />
						<div className="relative overflow-hidden rounded-[2rem] border border-white/[0.08] bg-white/[0.04] p-3 shadow-2xl shadow-black/30">
							<div className="mb-3 flex items-center justify-between rounded-2xl border border-white/[0.06] bg-[#111214] px-4 py-3 text-xs text-white/45">
								<div className="flex items-center gap-2">
									<span className="h-2.5 w-2.5 rounded-full bg-red-400/80" />
									<span className="h-2.5 w-2.5 rounded-full bg-yellow-400/80" />
									<span className="h-2.5 w-2.5 rounded-full bg-green-400/80" />
								</div>
								<span>Record → edit → export in one workflow</span>
							</div>
							<div className="glow-brand rounded-[1.4rem]">
								<video
									src={demoVideo}
									autoPlay
									loop
									muted
									playsInline
									preload="metadata"
									title="Clapio screen recorder tool demo"
									className="w-full rounded-[1.2rem]"
								/>
							</div>
						</div>
						<div className="absolute -bottom-6 -left-4 hidden rounded-2xl border border-white/[0.08] bg-[#0f1112]/90 px-4 py-3 backdrop-blur-xl md:block">
							<div className="text-[11px] uppercase tracking-[0.18em] text-white/35">
								Why it converts
							</div>
							<div className="mt-2 text-sm font-medium text-white/85">
								Cleaner videos with less editing overhead
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
